import React, { useEffect, useState } from 'react'
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    CircularProgress,
} from '@mui/material'
import UserApi from '../../../api/UserApi'
import { UserAdminDetail } from '@finance-lab/utils/types'
import { UserDevicesTable } from './UserDevicesTable'

const userApi = new UserApi()

function getApiErrorStatus(error: unknown): number | undefined {
    if (error instanceof Error && 'status' in error && typeof error.status === 'number') {
        return error.status
    }
    return undefined
}

function UserDevicesDialog({
    userId,
    userName,
    open,
    onClose,
}: {
    userId: string | null
    userName: string | null
    open: boolean
    onClose: () => void
}) {
    const [detail, setDetail] = useState<UserAdminDetail | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!open || !userId) {
            setDetail(null)
            setError(null)
            return
        }

        let cancelled = false
        setLoading(true)
        setError(null)

        void userApi
            .adminGetUser(userId)
            .then((response) => {
                if (!cancelled) {
                    setDetail(response)
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(
                        getApiErrorStatus(err) === 404 ? 'Användaren hittades inte.' : 'Kunde inte hämta enheter.',
                    )
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [open, userId])

    const devices = detail?.devices ?? []

    return (
        <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
            <DialogTitle>Enheter — {userName ?? detail?.name ?? ''}</DialogTitle>
            <DialogContent>
                {loading && (
                    <Typography sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                        <CircularProgress size={24} />
                        Laddar…
                    </Typography>
                )}
                {error && (
                    <Typography color='error' sx={{ py: 2 }}>
                        {error}
                    </Typography>
                )}
                {!loading && !error && (
                    <>
                        <Typography variant='body2' sx={{ mb: 2 }}>
                            {devices.length} registrerade enhet{devices.length === 1 ? '' : 'er'}
                        </Typography>
                        <UserDevicesTable devices={devices} />
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Stäng</Button>
            </DialogActions>
        </Dialog>
    )
}

export default UserDevicesDialog
